// Incident reporting (docs/plans/phase3-operations-modules.md §1.3). URLs carry internal
// IDs and the list filter only; what happened, root causes and notes travel in bodies.
import { queryOptions } from '@tanstack/react-query';
import {
  incidentListSchema,
  incidentOptionsSchema,
  incidentPeopleSchema,
  incidentSchema,
  operationsListItemSchema,
  type AddIncidentActionRequest,
  type ReportIncidentRequest,
  type SaveOperationsListRequest,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

export type IncidentFilter = 'open' | 'closed' | 'all';

export const incidentKeys = {
  all: ['incidents'] as const,
  options: ['incidents', 'options'] as const,
  people: ['incidents', 'people'] as const,
  mine: ['incidents', 'mine'] as const,
  list: (filter: IncidentFilter) => ['incidents', 'list', filter] as const,
  one: (id: string) => ['incidents', 'one', id] as const,
};

export function fetchIncidentOptions() {
  return api('GET', '/incidents/options', { schema: incidentOptionsSchema });
}

export const incidentOptionsQuery = queryOptions({
  queryKey: incidentKeys.options,
  queryFn: fetchIncidentOptions,
});

export const incidentPeopleQuery = queryOptions({
  queryKey: incidentKeys.people,
  queryFn: () => api('GET', '/incidents/people', { schema: incidentPeopleSchema }),
});

export const myIncidentsQuery = queryOptions({
  queryKey: incidentKeys.mine,
  queryFn: () => api('GET', '/incidents/mine', { schema: incidentListSchema }),
});

export function incidentListQuery(filter: IncidentFilter) {
  return queryOptions({
    queryKey: incidentKeys.list(filter),
    queryFn: () => api('GET', `/incidents?status=${filter}`, { schema: incidentListSchema }),
  });
}

export function incidentQuery(id: string) {
  return queryOptions({
    queryKey: incidentKeys.one(id),
    queryFn: () => api('GET', `/incidents/${id}`, { schema: incidentSchema }),
  });
}

export function reportIncident(body: ReportIncidentRequest) {
  return api('POST', '/incidents', { body, schema: incidentSchema });
}

const step = (id: string, path: string, body: object) =>
  api('POST', `/incidents/${id}/${path}`, { body, schema: incidentSchema });

export const incidentSteps = {
  startReview: (id: string, reviewerId: string) => step(id, 'review', { reviewerId }),
  rootCause: (id: string, rootCause: string) =>
    api('PUT', `/incidents/${id}/root-cause`, { body: { rootCause }, schema: incidentSchema }),
  addAction: (id: string, body: AddIncidentActionRequest) => step(id, 'actions', body),
  completeAction: (id: string, actionId: string, note: string) =>
    step(id, `actions/${actionId}/complete`, { note }),
  cancelAction: (id: string, actionId: string, reason: string) =>
    step(id, `actions/${actionId}/cancel`, { reason }),
  close: (id: string, closingNote: string) => step(id, 'close', { closingNote }),
  reopen: (id: string, reason: string) => step(id, 'reopen', { reason }),
  enteredInError: (id: string, reason: string) => step(id, 'entered-in-error', { reason }),
};

export function saveIncidentList(
  kind: 'incident_category' | 'incident_severity',
  body: SaveOperationsListRequest,
) {
  return api('PUT', `/incidents/options/${kind}`, {
    body,
    schema: z.array(operationsListItemSchema),
  });
}
